import { Transition } from "@headlessui/react";
import { MessageCircle } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import downArrow from "src/assets/down-arrow.svg";
import starEmpty from "src/assets/starEmpty.svg";
import SuccessIcon from "src/assets/successCircle.svg";
import { Autocomplete, Button, Checkbox, Input, Modal, Spinner } from "src/components";
import VersioningCrate from "src/components/Versioning/versioningModal.component";
import { invoiceActions } from "src/context/invoice-slice";
import { RootState } from "src/context/store";
import Download from "src/features/fields/assets/Download copy.svg";
import star from "src/features/fields/assets/star.svg";
import { useEventStatus } from "src/hooks/useEventStatus";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { useToast } from "src/hooks/useToast";
import {
  downloadInvoice,
  editInvoice,
  getInvoiceActionAllowed,
  getInvoiceChangeValidationV1,
  getInvoiceDetails,
  getSimilarOrders,
} from "src/services/invoice-api";
import Book from "../../assets/book.png";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import { DEFAULT_TABLE_DATA, getInvoiceDetailsInfo } from "../invoice-master/invoice-helper";
import InvoiceLayout from "../invoice-master/invoice-layout.component";
import InvoiceDetailCard from "./invoice-detail.component";
import InvoiceApprovalOrderedData from "./InvoiceApprovalCards/ApprovalOrderedData";
import NetAmountCompare from "./InvoiceApprovalCards/NetAmountCompare";
import RecallInvoice from "./InvoiceApprovalCards/RecallInvoice";
import RevertInvoice from "./InvoiceApprovalCards/RevertInvoice";
import InvoiceRemarks from "./InvoiceRemarks";
import PartialIcon from "src/assets/partial.svg";
import IsDifferentOrder from "./InvoiceApprovalCards/IsDifferentOrder";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";
import useDebounceLeading from "src/hooks/useDebounceLeading";

const subtleColors = ["#F9F9FF", "#EFF8FF", "#F3FFF4", "#FFF5F5", "#FDFCF7", "#F3F0FF", "#EFFFFA", "#FFF3F7", "#F0FBFF", "#F7F5FF"];

export const InvoiceApprovalScreen = () => {
  const { stockist_invoice_attachment_id } = useParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { invoiceApprovalData } = useSelector((state: RootState) => state.invoice);
  const [mappedData, setMappedData] = useState<any[]>([]);
  const [selectedItemDetails, setSelectedItemDetails] = useState<any>(null);
  const [tableData, setTableData] = useState(DEFAULT_TABLE_DATA);
  const { toast } = useToast();
  const [productsData, setProductsData] = useState<any[]>(invoiceApprovalData?.invoice?.invoiceProducts);
  const [orderedData, setOrderedData] = useState<any[]>([]);
  const [similarOrders, setSimilarOrders] = useState<any[]>([]);
  const [tcs, setTcs] = useState<number | any>(0);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const navigate = useNavigate();
  const [isRemarksShown, setIsRemarksShown] = useState(false);
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [VersioningModal, setVersioningModal] = useState(false);
  const [remainingQuantity, setRemainingQuantity] = useState<any[]>([]);
  const [revertInvoiceOpen, setRevertInvoiceOpen] = useState<boolean>(false);
  const [recallInvoiceOpen, setRecallInvoiceOpen] = useState<boolean>(false);
  const [openNetAmtModal, setOpenNetAmtModal] = useState<boolean>(false);
  const [entryNetAmt, setEntryNetAmt] = useState<string>("");
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set());
  const [isDifferentOrderModalOpen, setIsDifferentOrderModalOpen] = useState<boolean>(false);
  const [orderToSelect, setOrderToSelect] = useState<any[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMapped, setIsMapped] = useState(true); //M10-11117

  //M10-9862
  const location = useLocation();
  const { CompanyCode } = location.state || {};
  // const [isAutoApproving, setIsAutoApproving] = useState<boolean>(false);
  const company_code = invoiceApprovalData?.company_code;
  const { hasPermission: viewRemarks } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.module,
    action: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.action,
  });

  const getRowId = (item: any, index: number) => `${item.unique_key}-${index}`;

  const uniqueKeyColorMap: Record<string, string> = {};
  let colorIndex = 0;

  mappedData.forEach((item) => {
    if (!uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")]) {
      uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")] = subtleColors[colorIndex % subtleColors.length];
      colorIndex++;
    }
  });

  const outSideClickRef = useOutsideClick(() => {
    setVersioningModal(false);
  });
  const { withEventCheck } = useEventStatus();

  const inputRef = useRef<HTMLInputElement>(null);
  const ref = useOutsideClick(() => {
    setIsRemarksShown(false);
  });

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const fetchInvoiceDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getInvoiceDetails(stockist_invoice_attachment_id as string, false, CompanyCode);
      dispatch(invoiceActions.setInvoiceApprovalData(response?.data?.data));
      //M10-9685-MRP Related Issue
      const productsDataStateTobeSaved = response?.data?.data?.invoice?.invoiceProducts
        ?.filter((item: any) => item?.is_confirmed)
        ?.map((item: any) => ({
          ...item,
          unmappedQty: item?.quantity,
          unmappedFreeQty: item?.free_qty,
          unique_key: `${item?.item_code}~${item?.batch_no}`,
        }));
      //M10-9685-MRP Related Issue
      setProductsData(productsDataStateTobeSaved);
      setEntryNetAmt(response?.data?.data?.invoice?.net_amount);
      setOrderedData(
        response?.data?.data?.invoice?.invoiceProducts?.flatMap(
          (item: any) =>
            item?.orderProductMappings?.map((mapping: any) => ({
              ...mapping,
              item_description: item?.item_description,
              item_code: item?.item_code,
              inv_mrp: item?.inv_mrp,
              ptr: item?.ptr,
              price_identifier: item?.discount_type,
              batch_no: item?.batch_no,
            })) || []
        )
      );
      if (response?.data?.data?.invoice?.auto_approved?.length > 0) {
        setMappedData(response?.data?.data?.invoice?.auto_approved);
        toast({ title: "Auto approving", description: "Invoice products are auto approved", variant: "default" });
      }
      // if (response?.data?.data?.fileStatusHdr?.fuid_status_id !== 8) {
      //   handleAutoApprovalOfSchemeTypeInvoiceAndOrder(productsDataStateTobeSaved, response?.data?.data);
      // }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (e: any, item: any, index: number) => {
    const rowId = getRowId(item, index);

    setCheckedRows((prev) => {
      const newSet = new Set(prev);
      if (e.target.checked) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });

    if (e.target.checked) {
      setSelectedProducts((prev) => {
        const existing = prev.find((p) => p.unique_key === item.unique_key);
        if (existing) {
          return prev.map((p) =>
            p.unique_key === item.unique_key
              ? {
                ...p,
                quantity: p.quantity + item.quantity,
                free_qty: p.free_qty + item.free_qty,
                unmappedQty: p.unmappedQty + item.quantity,
                unmappedFreeQty: p.unmappedFreeQty + item.free_qty,
              }
              : p
          );
        } else {
          return [...prev, { ...item }];
        }
      });
    } else {
      // Unchecking this row — need to subtract this item's quantities
      setSelectedProducts((prev) => {
        const updated = prev.map((p) => {
          if (p.unique_key === item.unique_key) {
            return {
              ...p,
              quantity: p.quantity - item.quantity,
              free_qty: p.free_qty - item.free_qty,
              unmappedQty: p.unmappedQty - item.quantity,
              unmappedFreeQty: p.unmappedFreeQty - item.free_qty,
            };
          }
          return p;
        });

        // If quantities zeroed, remove the item
        return updated.filter((p) => p.quantity > 0 || p.free_qty > 0);
      });
    }
  };

  const calculateTotalCashDiscountPercentage = () => {
    const totalCashDiscount = calculateTotalCashDiscount();
    const totalSubtotal = calculateSubtotal();
    return (totalCashDiscount / totalSubtotal) * 100;
  };

  const calculateTotalCashDiscount = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const { gross_amt, discount_perc, p_cash_discount_perc } = item;
      const afterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
      const cashDiscountAmount = (afterItemDiscount * Number(p_cash_discount_perc)) / 100;
      total += cashDiscountAmount;
    });
    return total;
  };

  // const subtotal = mappedData?.reduce((acc: number, item: any) => acc + Number(item?.quantity) * Number(item?.ptr), 0);
  const calculateSubtotal = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const { gross_amt, discount_perc } = item;
      const cashDiscountAmount = (gross_amt * Number(discount_perc)) / 100;
      total += gross_amt - cashDiscountAmount;
    });
    return total;
  };

  const calculateTotalGst = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const { gross_amt, p_gst_perc, discount_perc, p_cash_discount_perc } = item;
      // Calculate GST on amount after discount
      let amountAfterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
      let amountAfterCashDiscount = amountAfterItemDiscount - (amountAfterItemDiscount * Number(p_cash_discount_perc)) / 100;

      const gstAmount = (amountAfterCashDiscount * Number(p_gst_perc)) / 100;
      total += gstAmount;
    });
    return total;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const amountAfterCashDiscount = subtotal - calculateTotalCashDiscount();
    const totalGst = calculateTotalGst();
    const tcsCalculatedAmount = amountAfterCashDiscount + totalGst + ((amountAfterCashDiscount + totalGst) * tcs) / 100;
    // return amountAfterCashDiscount + totalGst;
    return tcsCalculatedAmount;
  };

  const calculateTotalCashDiscountNew = () => {
    let total = 0;
    invoiceApprovalData?.invoice?.invoiceProducts?.forEach((item: any) => {
      const { gross_amt, discount_perc, p_cash_discount_perc } = item;
      const afterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
      const cashDiscountAmount = (afterItemDiscount * Number(p_cash_discount_perc)) / 100;
      total += cashDiscountAmount;
    });
    return total;
  };

  const calculateTotalGstnew = () => {
    let total = 0;
    invoiceApprovalData?.invoice?.invoiceProducts?.forEach((item: any) => {
      const { gross_amt, p_gst_perc, discount_perc, p_cash_discount_perc } = item;
      // Calculate GST on amount after discount
      let amountAfterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
      let amountAfterCashDiscount = amountAfterItemDiscount - (amountAfterItemDiscount * Number(p_cash_discount_perc)) / 100;

      const gstAmount = (amountAfterCashDiscount * Number(p_gst_perc)) / 100;
      total += gstAmount;
    });
    return total;
  };
  const calculateTotalAsFinal = () => {
    const subtotal = Number(invoiceApprovalData?.invoice?.gross_amount || 0);
    const amountAfterCashDiscount = subtotal - calculateTotalCashDiscountNew();
    const totalGst = calculateTotalGstnew();
    const tcsCalculatedAmount = amountAfterCashDiscount + totalGst + ((amountAfterCashDiscount + totalGst) * tcs) / 100;
    // return amountAfterCashDiscount + totalGst;
    return tcsCalculatedAmount;
  };

  const handleViewDetails = async (item: any) => {
    setSelectedOrders([]);
    setSelectedItemDetails(null);
    // setMappedData((prev) => prev.filter((mappedItem) => mappedItem.unique_key !== item.unique_key));
    try {
      const response = await getSimilarOrders({
        item_code: item?.item_code,
        inv_mrp: item?.inv_mrp,
        counter_code: invoiceApprovalData?.counter_code,
        company_stockist_code: invoiceApprovalData?.company_stockist_code,
        invoice_date: invoiceApprovalData?.invoice?.new_invoice_date,
        company_code: invoiceApprovalData?.company_code,
      });

      toast({
        title: "Data Fetched Successfully",
        description: "Data has been fetched successfully.",
      });

      setSimilarOrders(response?.data?.data);
      setSelectedItemDetails(item);

      //if unique_key is not present in remainingQuantity, then add it
      const incomingKey = item?.unique_key?.split("~")[0];

      setRemainingQuantity((prev) => {
        const existingIndex = prev.findIndex((product) => product?.unique_key === incomingKey);

        if (existingIndex !== -1) {
          // Update only the required fields
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            remaining_invoice_quantity: item?.quantity,
            remaining_invoice_free_quantity: item?.free_qty,
          };
          return updated;
        } else {
          // Add new entry if it doesn't exist
          return [
            ...prev,
            {
              unique_key: incomingKey,
              orders: response?.data?.data?.map((orderItem: any) => ({
                order_code: orderItem?.order?.order_code,
                remaining_quantity: orderItem?.remaining_quantity,
                remaining_free_quantity: orderItem?.remaining_free_quantity,
              })),
              remaining_invoice_quantity: item?.quantity,
              remaining_invoice_free_quantity: item?.free_qty,
            },
          ];
        }
      });

      requestAnimationFrame(() => {
        setTimeout(() => {
          if (inputRef.current) {
            // Store the original styles
            const originalBorder = inputRef.current.style.border || "1px solid #d1d5db"; // Default Tailwind gray-300
            const originalBoxShadow = inputRef.current.style.boxShadow || "none";

            // Focus and apply new styles
            inputRef.current.focus();
            inputRef.current.style.boxShadow = "0 0 0 2px #8051FF";

            // Add blur event listener to reset styles
            const resetStyles = () => {
              if (inputRef.current) {
                inputRef.current.style.border = originalBorder;
                inputRef.current.style.boxShadow = originalBoxShadow;
              }
            };

            // Remove any existing listener to prevent duplicates
            inputRef.current.removeEventListener("blur", resetStyles);
            // Add the blur listener
            inputRef.current.addEventListener("blur", resetStyles, { once: true });
          }
        }, 100);
      });
      setShowOrderDropdown(true);
    } catch (error: any) {
      console.log(error);
      toast({
        title: error?.response?.data?.error,
        description: error?.response?.data?.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (!selectedItemDetails) return;
    let modifiedQuantity;
    let modifiedFreeQty;

    let remainingQty;
    let OrdRemainingFreeQty;
    let OrdDiscountType;
    let OrdQuantity;

    let discountInv;
    let discountOrd;
    let InvQty;
    let InvFreeQty;
    let fixed_price;
    let InvDiscPerc;
    let InvDiscAmt;
    let InvDiscountType;

    let selectedItemUniqueKey = selectedItemDetails?.unique_key?.split("~")[0];

    let modifiedDiscountPercent;
    let cashDiscountInvoice = Number(selectedItemDetails?.p_cash_discount_perc) || 0;
    let cashDiscountOrder = Number(selectedOrders?.[0]?.order?.order_cash_discount_perc) || 0;

    remainingQty = remainingQuantity
      ?.find((item) => item?.unique_key === selectedItemUniqueKey)
      ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;

    OrdRemainingFreeQty = remainingQuantity
      ?.find((item) => item?.unique_key === selectedItemUniqueKey)
      ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity;

    InvQty = selectedItemDetails?.quantity;
    InvFreeQty = selectedItemDetails?.free_qty;
    InvDiscPerc = (selectedItemDetails?.discount_amt / selectedItemDetails?.gross_amt) * 100;
    InvDiscAmt = selectedItemDetails?.discount_amt;
    InvDiscountType = selectedItemDetails?.discount_type;

    OrdDiscountType = selectedOrders?.[0]?.price_identifier;
    OrdQuantity = selectedOrders?.[0]?.quantity;

    discountInv = String(((selectedItemDetails?.discount_amt / selectedItemDetails?.gross_amt) * 100)?.toFixed(2));

    //When invoice discount type is Discount Percent and order is of scheme then allow equivalent free qty of discount of invoiced quantity
    if ((InvDiscountType == "Discount Percent" || InvDiscountType == "Discount Amount") && InvDiscPerc !== 0 && OrdDiscountType === 2) {
      const allowedFreeQtyRaw = InvQty * (InvDiscPerc / 100);
      modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      modifiedQuantity = InvQty - modifiedFreeQty > remainingQty ? remainingQty : InvQty - modifiedFreeQty;
    }
    //When invoice discount type is Scheme and order is of Discount percent then allow equivalent free qty of discount of ordered quantity
    else if (InvDiscountType == "Scheme" && OrdDiscountType === 1) {
      // const allowedFreeQtyRaw = Math.round(OrdQuantity * (selectedOrders?.[0]?.discount_on_ptr / 100)); //vijay changes
      modifiedFreeQty = OrdRemainingFreeQty;
      modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
      // modifiedQuantity = InvQty + allowedFreeQtyRaw; //vijay changes
      // modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      // modifiedQuantity = OrdQuantity - modifiedFreeQty;
    }
    //When invoice discount type is Fixed price and order is of Fixed Price
    else if (
      (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent" || InvDiscountType == "Scheme") &&
      InvDiscPerc == 0 &&
      InvDiscAmt == 0 &&
      InvFreeQty == 0 &&
      OrdDiscountType === 3
    ) {
      const calculatedQuantity = (remainingQty * selectedOrders?.[0]?.fixed_price) / selectedItemDetails?.inv_rate;
      modifiedQuantity = Math.round(Math.min(selectedItemDetails?.quantity, calculatedQuantity));
      // modifiedFreeQty = remainingQty - modifiedQuantity;
    }
    //When invoice discount type is Fixed price and order is of Scheme then allow equivalent free qty of discount of invoiced quantity
    else if (
      (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
      InvDiscPerc == 0 &&
      InvDiscAmt == 0 &&
      OrdDiscountType === 2
    ) {
      const absDiffInPtrAndPrice = Math.abs(selectedOrders?.[0]?.ptr - selectedItemDetails?.inv_rate);
      const allowedFreeQtyRaw = (InvQty * ((absDiffInPtrAndPrice / selectedOrders?.[0]?.ptr) * 100)) / 100;
      modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      modifiedQuantity = Math.min(InvQty - modifiedFreeQty, remainingQty);
    }
    //When invoice discount type is Scheme  and order is of Fixed price then allow equivalent free qty of discount of ordered quantity
    else if (InvDiscountType == "Scheme" && OrdDiscountType === 3) {
      const absDiffInPtrAndPrice = Math.abs(selectedOrders?.[0]?.fixed_price - selectedItemDetails?.ptr);
      const allowedFreeQtyRaw = (OrdQuantity * ((absDiffInPtrAndPrice / selectedItemDetails?.ptr) * 100)) / 100;
      modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      modifiedQuantity = OrdQuantity - modifiedFreeQty;
    }
    //When invoice discount type is Fixed price and order is Discount on PTR
    else if (
      (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
      InvDiscPerc == 0 &&
      InvDiscAmt == 0 &&
      OrdDiscountType === 1
    ) {
      // const invoiceAmount = InvQty * InvRate;
      // const orderAmount = ((OrdQuantity * selectedOrders?.[0]?.ptr)) - ((OrdQuantity * selectedOrders?.[0]?.ptr) * (selectedOrders?.[0]?.discount_on_ptr / 100));
      //if difference of invoice amount and order amount is less than or equal to 1 then allow the quantity of order
      // if (Math.abs(invoiceAmount - orderAmount) <= 1) {
      modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
      modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
      // }
    }
    //When invoice discount type is Discount on PTR and order is of Fixed price
    else if ((InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") && InvDiscPerc !== 0 && OrdDiscountType === 3) {
      // const invoiceAmount = (InvQty * InvRate) - (InvQty * InvRate) * (InvDiscPerc / 100);
      // const orderAmount = (OrdQuantity * selectedOrders?.[0]?.fixed_price);
      //if difference of invoice amount and order amount is less than or equal to 1 then allow the quantity of order
      // if (Math.abs(invoiceAmount - orderAmount) <= 1) {
      modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
      modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
      // }
    } else {
      if (OrdDiscountType === 2) {
        let totalDiscountPercentage1 = cashDiscountInvoice + ((100 - cashDiscountInvoice) * Number(discountInv)) / 100;
        const allowedFreeQtyRaw = InvQty * (Number(totalDiscountPercentage1) / 100);
        let calculatedFreeQty = Number(Math.round(allowedFreeQtyRaw) + InvFreeQty);
        modifiedFreeQty = Math.min(calculatedFreeQty, OrdRemainingFreeQty);
        modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
      } else {
        modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
        modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
      }
    }

    discountOrd = (() => {
      switch (OrdDiscountType) {
        case 1:
          return selectedOrders?.[0]?.discount_on_ptr;
        case 3:
          return "Fixed Price : " + selectedOrders?.[0]?.fixed_price;
        default:
          return "0";
      }
    })();

    fixed_price = (() => {
      switch (OrdDiscountType) {
        case 3:
          return selectedItemDetails?.ptr;
        default:
          return selectedItemDetails?.ptr;
      }
    })();

    //different percentage between ptr and price
    const percentageDifference = Math.abs(((selectedItemDetails?.inv_rate - selectedItemDetails?.ptr) / selectedItemDetails?.ptr) * 100).toFixed(2);
    let totalDiscountPercentage1 = cashDiscountInvoice + ((100 - cashDiscountInvoice) * Number(discountInv)) / 100;
    let totalDiscountPercentage = Number(percentageDifference) + ((100 - Number(percentageDifference)) * Number(totalDiscountPercentage1)) / 100;

    const cashDiscountModifiedData = Number(cashDiscountOrder);
    let totalDiscountcalculationWithoutScheme = Number(
      (Number(totalDiscountPercentage) - Number(cashDiscountOrder)) / (1 - Number(cashDiscountOrder) / 100)
    ).toFixed(2);

    let schemeFree = (Number(selectedItemDetails?.free_qty / (selectedItemDetails?.free_qty + InvQty)) * 100).toFixed(2);
    let totalDiscountcalculation = Number(
      Number(totalDiscountcalculationWithoutScheme) + ((100 - Number(totalDiscountcalculationWithoutScheme)) * Number(schemeFree)) / 100
    ).toFixed(2);
    //=G27/(G27+F27)

    // if (InvDiscountType == "Scheme" && OrdDiscountType == 1) {
    //   modifiedDiscountPercent = discountOrd;
    // } else {
    //   let discountVal = (100 * cashDiscountModifiedData - 100 * cashDiscountInvoice) / (cashDiscountModifiedData - 100)
    //   // modifiedDiscountPercent = OrdDiscountType === 3 ? "0" : discountInv < discountOrd ? discountInv : discountOrd;
    //   let modifiedDataPer = OrdDiscountType === 3 ? "0" : discountInv < discountOrd ? discountInv : discountOrd
    //   modifiedDiscountPercent = Number(
    //     Math.min(modifiedDataPer, Number(discountVal)).toFixed(2)
    //   );
    // }
    if (InvDiscountType == "Scheme" && OrdDiscountType == 1) {
      modifiedDiscountPercent = discountOrd;
    } else {
      modifiedDiscountPercent = Math.min(Number(totalDiscountcalculation), Number(discountOrd) || 0);
    }
    let modified_price = selectedItemDetails?.ptr;
    if (OrdDiscountType == 3) {
      if (selectedOrders?.[0]?.fixed_price) {
        // =I15*(100-J15)/100
        const calculationData = Number((Number(selectedItemDetails?.inv_rate) * (100 - Number(discountInv))) / 100).toFixed(2);
        modified_price = Math.max(Number(calculationData), selectedOrders?.[0]?.fixed_price);
      }
    }

    // modifiedFixedPrice = Number(selectedItemDetails?.inv_rate) > Number(fixed_price) ? selectedItemDetails?.inv_rate : fixed_price;

    const newTableData = [
      {
        type: "Quantity",
        invoiceData: selectedItemDetails?.quantity,
        orderedData: String(selectedOrders?.reduce((acc: number, curr: any) => acc + Number(curr?.quantity), 0)),
        // modifiedData: selectedItemDetails?.quantity,

        modifiedData: modifiedQuantity,
        remaining_quantity: remainingQty,
        allowed_quantity: modifiedQuantity,
      },
      {
        type: "Discount %",
        invoiceData: discountInv,
        orderedData: discountOrd,
        modifiedData: modifiedDiscountPercent,
      },
      {
        type: "Cash Discount",
        invoiceData: cashDiscountInvoice,
        orderedData: cashDiscountOrder,
        // modifiedData: selectedItemDetails?.p_cash_discount_perc || 0,
        modifiedData: cashDiscountModifiedData,
        // modifiedData: data?.item_discount,
      },
      {
        type: "Scheme Qty",
        invoiceData: selectedItemDetails?.free_qty,
        orderedData: OrdDiscountType === 2 ? selectedOrders?.[0]?.free_quantity : "0",
        // modifiedData: selectedItemDetails?.free_qty,
        modifiedData: modifiedFreeQty,
        allowed_free_qty: modifiedFreeQty,
      },
      {
        type: "PTR",
        invoiceData: selectedItemDetails?.ptr,
        // orderedData: selectedOrders?.[0]?.ptr,
        orderedData: selectedItemDetails?.ptr,
        modifiedData: selectedItemDetails?.ptr,
      },
      {
        type: "MRP",
        invoiceData: selectedItemDetails?.inv_mrp,
        orderedData: selectedItemDetails?.inv_mrp,
        // orderedData: selectedOrders?.[0]?.mrp,
        modifiedData: selectedItemDetails?.inv_mrp,
      },
      {
        type: "Price",
        invoiceData: selectedItemDetails?.inv_rate,
        orderedData: fixed_price,
        modifiedData: String(modified_price),
      },
      {
        type: "Calculated Value",
        invoiceData: Number(
          selectedItemDetails?.quantity * selectedItemDetails?.inv_rate * (1 - Number(discountInv) / 100) * (1 - cashDiscountInvoice / 100)
        ).toFixed(2),
        orderedData: "-",
        modifiedData: Number(
          modifiedQuantity * modified_price * (1 - Number(modifiedDiscountPercent) / 100) * (1 - cashDiscountModifiedData / 100)
        ).toFixed(2),
      },
    ];

    setTableData(newTableData);
  }, [selectedOrders]);

  //On change of modified data
  const handleModifyData = (index: number, value: string) => {
    const updatedTableData = [...tableData];

    const currentType = tableData[index].type;
    const newValue = Number(value);

    //Scheme Validation
    if (currentType === "Scheme Qty") {
      const orderedFreeQuantity = Number(
        remainingQuantity
          ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
          ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity
      );
      const remainingInvoicedFreeQuantity = Number(
        remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])?.remaining_invoice_free_quantity
      );

      if (selectedItemDetails?.discount_type == "Scheme" && selectedOrders?.[0]?.price_identifier !== 2) {
        if (remainingInvoicedFreeQuantity < orderedFreeQuantity && newValue > remainingInvoicedFreeQuantity) {
          toast({
            description: `Modified Scheme Qty cannot be greater than remaining invoice free quantity ${remainingInvoicedFreeQuantity}`,
            variant: "destructive",
          });
        }
      }

      if (selectedItemDetails?.discount_type == "Scheme" && selectedOrders?.[0]?.price_identifier !== 1) {
        if (orderedFreeQuantity < remainingInvoicedFreeQuantity && newValue > orderedFreeQuantity) {
          toast({
            description: `Modified Scheme Qty cannot be greater than remaining order free quantity ${orderedFreeQuantity}`,
            variant: "destructive",
          });
        }
      }

      if (isNaN(newValue) || newValue < 0) {
        toast({
          title: "Invalid Scheme Quantity",
          description: "Please enter a valid positive number",
          variant: "destructive",
        });
        return;
      }
    }

    // Quantity validation
    if (currentType === "Quantity") {
      const orderedQuantity = Number(
        remainingQuantity
          ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
          ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity
      );

      // const remainingInvoicedQuantity = Number(
      //   remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])?.remaining_invoice_quantity
      // );

      const remainingInvoicedQuantity = selectedItemDetails?.unmappedQty;

      if (newValue > remainingInvoicedQuantity) {
        toast({
          description: `Modified quantity cannot be greater than remaining invoice quantity ${remainingInvoicedQuantity}`,
          variant: "destructive",
        });
      }

      if (newValue > orderedQuantity) {
        toast({
          description: `Modified quantity cannot be greater than remaining order quantity ${orderedQuantity}`,
          variant: "destructive",
        });
      }

      if (isNaN(newValue) || newValue < 0) {
        toast({
          title: "Invalid Quantity",
          description: "Please enter a valid positive number",
          variant: "destructive",
        });
        return;
      }
    }

    // Discount % validation
    if (currentType === "Discount %") {
      const orderedDiscountType = Number(tableData[index].orderedData);
      if (newValue > orderedDiscountType) {
        toast({
          description: `Modified discount % cannot be greater than ordered discount % ${orderedDiscountType}`,
          variant: "destructive",
        });
      }
    }

    //Price Validation
    if (currentType === "Price") {
      const orderedPrice = Number(tableData[index].orderedData);
      const InvoicePrice = Number(tableData[index].invoiceData);
      if (orderedPrice > InvoicePrice && newValue > orderedPrice) {
        toast({
          description: `Modified Price cannot be greater than ordered price ${orderedPrice}`,
          variant: "destructive",
        });
      }
      if (InvoicePrice > orderedPrice && newValue > InvoicePrice) {
        toast({
          description: `Modified Price cannot be greater than ordered price ${InvoicePrice}`,
          variant: "destructive",
        });
      }
    }

    // Update the current modified value
    updatedTableData[index].modifiedData = value;

    // Mutual reset logic
    const discountIndex = tableData.findIndex((item) => item.type === "Discount %");
    const schemeIndex = tableData.findIndex((item) => item.type === "Scheme Qty");

    if (currentType === "Discount %" && schemeIndex !== -1) {
      updatedTableData[schemeIndex].modifiedData = "0";
    }

    if (currentType === "Scheme Qty" && discountIndex !== -1) {
      updatedTableData[discountIndex].modifiedData = "0";
    }
    // 🔽 Recalculate "Calculated Value"
    const quantityRow = updatedTableData.find((item) => item.type === "Quantity");
    const priceRow = updatedTableData.find((item) => item.type === "Price");
    const discountRow = updatedTableData.find((item) => item.type === "Discount %");
    const cashDiscountRow = updatedTableData.find((item) => item.type === "Cash Discount");
    const calculatedValueRowIndex = updatedTableData.findIndex((item) => item.type === "Calculated Value");

    const modifiedQuantity = Number(quantityRow?.modifiedData || 0);
    const modifiedPrice = Number(priceRow?.modifiedData || 0);
    const modifiedDiscount = Number(discountRow?.modifiedData || 0);
    const modifiedCashDiscount = Number(cashDiscountRow?.modifiedData || 0);

    const modifiedCalculatedValue = (
      modifiedQuantity *
      modifiedPrice *
      (1 - modifiedDiscount / 100) *
      (1 - modifiedCashDiscount / 100)
    ).toFixed(2);

    if (calculatedValueRowIndex !== -1) {
      updatedTableData[calculatedValueRowIndex].modifiedData = modifiedCalculatedValue;
    }

    setTableData(updatedTableData);
  };

  const handleSaveModifiedData = () => {
    setTcs(Number(invoiceApprovalData?.invoice?.tcs_perc));
    if (!selectedItemDetails) return;

    if (selectedOrders?.length == 0) {
      toast({ description: "Choose an order before submission", variant: "destructive" });
      return;
    }

    const getRemainingItem = () => {
      return remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0]);
    };

    const showError = (message: string) => {
      toast({ description: message, variant: "destructive" });
      return true;
    };

    const hasInvalidQuantity = tableData.some((item) => {
      const modifiedValue = Number(item?.modifiedData);
      const invoiceValue = Number(item?.invoiceData);
      const orderedValue = Number(item?.orderedData);
      // const allowedQty = Number(item?.allowed_qty);
      // const allowedFreeQty = Number(item?.allowed_free_qty);
      const remainingItem = getRemainingItem();
      const remainingOrderQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;
      // const remainingInvoiceQuantity = remainingItem?.remaining_invoice_quantity;
      const remainingInvoiceQuantity = selectedItemDetails?.unmappedQty;
      const remainingFreeQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)
        ?.remaining_free_quantity;
      const InvDiscountType = selectedItemDetails?.discount_type;
      const OrdDiscountType = selectedOrders?.[0]?.price_identifier;

      // Quantity validations
      if (item?.type === "Quantity") {
        if (modifiedValue > remainingOrderQuantity) {
          return showError("Modified quantity cannot be greater than remaining order quantity");
        }
        if (!(InvDiscountType === "Scheme" && OrdDiscountType === 1) && modifiedValue > remainingInvoiceQuantity) {
          return showError(`Modified quantity cannot be greater than remaining invoice quantity ${remainingInvoiceQuantity}`);
        }
        if (!(InvDiscountType === "Scheme" && OrdDiscountType === 1) && modifiedValue > invoiceValue) {
          return showError("Quantity cannot be greater than invoiced quantity");
        }
        // if (modifiedValue > allowedQty) {
        //   return showError("Quantity cannot be greater than allowed quantity");
        // }
      }

      // Scheme Qty validations
      if (item?.type === "Scheme Qty") {
        if (
          !(InvDiscountType === "Scheme" && OrdDiscountType === 3) &&
          !(InvDiscountType === "Scheme" && OrdDiscountType === 1) &&
          modifiedValue > remainingFreeQuantity
        ) {
          return showError("Modified free quantity cannot be greater than remaining free quantity");
        }

        // if (!(((InvDiscountType === "Discount Percent" || InvDiscountType === "Discount Amount") && Number(selectedItemDetails?.discount_amt) === 0) && (OrdDiscountType === 2)) &&
        //   !(((InvDiscountType === "Discount Percent" || InvDiscountType === "Discount Amount") && Number(selectedItemDetails?.discount_amt) !== 0) && (OrdDiscountType === 2)) &&
        //   (modifiedValue > invoiceValue)) {
        //   return showError("Scheme Qty cannot be greater than invoiced quantity");
        // }

        if (
          !(InvDiscountType === "Scheme" && OrdDiscountType === 3) &&
          !(InvDiscountType === "Scheme" && OrdDiscountType === 1) &&
          modifiedValue > orderedValue
        ) {
          return showError("Scheme Qty cannot be greater than ordered quantity");
        }
      }

      //   if (modifiedValue > remainingFreeQuantity && OrdDiscountType !== 3 &&
      //     InvDiscountType === "Scheme" &&
      //     OrdDiscountType !== 1
      //   ) {
      //     return showError("Modified free quantity cannot be greater than remaining free quantity");
      //   }

      //   if ((OrdDiscountType !== 3 && InvDiscountType === "Scheme") &&
      //     (OrdDiscountType !== 2 && InvDiscountType !== "Scheme")) {
      //     if (modifiedValue > invoiceValue) {
      //       return showError("Scheme Qty cannot be greater than invoiced quantity");
      //     }
      //   }

      //   if (modifiedValue > orderedValue && OrdDiscountType !== 3 &&
      //     InvDiscountType === "Scheme" &&
      //     OrdDiscountType !== 1
      //   ) {
      //     return showError("Scheme Qty cannot be greater than ordered quantity");
      //   }
      // }

      // Price validations
      if (item?.type === "Price") {
        if (invoiceValue > orderedValue && modifiedValue > invoiceValue) {
          return showError("Price cannot be greater than invoiced price");
        }
        if (orderedValue > invoiceValue && modifiedValue > orderedValue) {
          return showError("Price cannot be greater than order price");
        }
      }

      // Discount validations
      if (item?.type === "Discount %" && modifiedValue > orderedValue) {
        return showError("Discount cannot be greater than ordered discount");
      }

      if (item?.type === "Cash Discount") {
        if (orderedValue < modifiedValue) {
          return showError("Cash Discount cannot be greater");
        }
      }

      return false;
    });

    if (hasInvalidQuantity) {
      return;
    }

    let modifiedDiscountType = "";
    if (selectedOrders?.[0]?.price_identifier === 1) {
      modifiedDiscountType = "Discount Percent"
    } else if (selectedOrders?.[0]?.price_identifier === 2) {
      modifiedDiscountType = "Scheme"
    } else {
      modifiedDiscountType = "Fixed Price";
    }
    // console.log(selectedOrders,"VIJAYA KUMAR")
    // if ((selectedItemDetails?.discount_type == "Scheme", selectedOrders?.[0]?.price_identifier === 1)) {
    //   modifiedDiscountType = "Discount Percent";
    // } else if (
    //   selectedOrders?.[0]?.price_identifier === 2 &&
    //   (selectedItemDetails?.discount_type == "Discount Percent" || selectedItemDetails?.discount_type == "Discount Amount")
    // ) {
    //   modifiedDiscountType = "Scheme";
    // } else if (
    //   Math.abs(Number(selectedItemDetails?.inv_rate) - Number(selectedItemDetails?.ptr)) > 1
    // ) {
    //   modifiedDiscountType = "Fixed Price";
    // } else {
    //   modifiedDiscountType = selectedItemDetails?.discount_type;
    // }

    const orderCodes = selectedOrders?.map((item: any) => item?.id);

    const baseKey = `${selectedItemDetails?.item_code}~${selectedItemDetails?.batch_no}~${selectedOrders?.[0]?.id}`;
    const existingCount = mappedData.filter((item) => item.unique_key?.startsWith(baseKey)).length;
    const finalUniqueKey = `${baseKey}~${existingCount + 1}`;
    const modifiedQuantity = Number(tableData.find((item) => item.type === "Quantity")?.modifiedData) || 0;
    const modifiedFreeQty = Number(tableData.find((item) => item.type === "Scheme Qty")?.modifiedData) || 0;
    let grossAmount = Number(tableData.find((item) => item.type === "Price")?.modifiedData) * Number(modifiedQuantity);
    // if (
    //   selectedOrders?.[0]?.price_identifier === 3 &&
    //   Number(selectedItemDetails?.discount_perc) === 0 &&
    //   Number(selectedItemDetails?.discount_amt) === 0 &&
    //   Number(selectedItemDetails?.free_qty) === 0 &&
    //   Number(selectedItemDetails?.inv_rate) < Number(selectedItemDetails?.ptr)
    // ) {
    //   console.log("If",Number(tableData.find((item) => item.type === "Price")?.modifiedData) * Number(modifiedQuantity))
    //   grossAmount = Number(tableData.find((item) => item.type === "Price")?.modifiedData) * Number(modifiedQuantity);
    // } else {
    //   console.log(tableData,"00000")
    //   console.log("else",Number(tableData.find((item) => item.type === "PTR")?.modifiedData) * Number(modifiedQuantity))
    //   grossAmount = Number(tableData.find((item) => item.type === "PTR")?.modifiedData) * Number(modifiedQuantity);
    // }

    const modifiedItem = {
      ...selectedItemDetails,
      quantity: modifiedQuantity,
      free_qty: modifiedFreeQty,
      discount_perc: Number(tableData.find((item) => item.type === "Discount %")?.modifiedData),
      discount_type: modifiedDiscountType,
      cash_discount: tableData.find((item) => item.type === "Cash Discount")?.modifiedData,
      ptr: tableData.find((item) => item.type === "PTR")?.modifiedData,
      inv_mrp: tableData.find((item) => item.type === "MRP")?.modifiedData,
      inv_rate: tableData.find((item) => item.type === "Price")?.modifiedData,
      gross_amt: grossAmount,
      fixed_price: modifiedDiscountType === 'Fixed Price' ? tableData.find((item) => item.type === "Price")?.modifiedData : null,
      // selectedOrders?.[0]?.price_identifier === 3 &&
      //   Number(selectedItemDetails?.discount_perc) === 0 &&
      //   Number(selectedItemDetails?.discount_amt) === 0 &&
      //   Number(selectedItemDetails?.free_qty) === 0 &&
      //   Number(selectedItemDetails?.inv_rate) < Number(selectedItemDetails?.ptr)
      //   ? tableData.find((item) => item.type === "Price")?.modifiedData
      //   : (() => {
      //     switch (selectedItemDetails?.discount_type) {
      //       case "Scheme":
      //         return Number(
      //           selectedItemDetails?.ptr -
      //           (selectedItemDetails?.ptr * ((selectedItemDetails?.free_qty / selectedItemDetails?.quantity) * 100)) / 100
      //         );
      //       default:
      //         if (Number(selectedItemDetails?.inv_rate) < Number(selectedItemDetails?.ptr)) {
      //           return tableData.find((item) => item.type === "Price")?.modifiedData
      //         }
      //         return Number(
      //           selectedItemDetails?.ptr -
      //           (selectedItemDetails?.ptr * ((selectedItemDetails?.discount_amt / selectedItemDetails?.gross_amt) * 100)) / 100
      //         );
      //     }
      //   })(),
      invoice_id: invoiceApprovalData?.invoice?.invoice_id,
      company_stockist_code: String(invoiceApprovalData?.company_stockist_code),
      counter_code: invoiceApprovalData?.counter_code,
      order_code: orderCodes,
      p_cash_discount_perc: Number(tableData.find((item) => item.type === "Cash Discount")?.modifiedData),
      unique_key: finalUniqueKey,
    };

    setProductsData((prev) =>
      prev.map((item) => {
        const modifiedBaseKey = modifiedItem.unique_key?.split("~").slice(0, 2).join("~");

        if (item.unique_key !== modifiedBaseKey) return item;
        const InvDiscountType = selectedItemDetails?.discount_type;
        const OrdDiscountType = selectedOrders?.[0]?.price_identifier;

        let status = "not_invoiced";
        let unmappedQty;
        let unmappedFreeQty;

        //Inv -> Discount on PTR && Ord -> Scheme
        if (
          (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
          selectedItemDetails?.discount_amt !== 0 &&
          OrdDiscountType == 2
        ) {
          if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          }
        }

        //Inv -> Fixed Price && Ord -> Scheme
        else if (
          (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
          selectedItemDetails?.discount_amt === 0 &&
          OrdDiscountType === 2
        ) {
          if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          }
        }

        //Inv -> scheme Ord -> Discount on PTR
        else if (InvDiscountType == "Scheme" && OrdDiscountType === 1) {
          // if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
          //   status = "partially_invoiced";
          //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
          //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          // }
          // else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
          //   status = "fully_invoiced";
          //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
          //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          // }
          if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty + selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = 0;
            unmappedFreeQty = 0;
          } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty + selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        //Inv -> Fixed Price && Ord -> Discount on PTR
        else if (
          (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
          selectedItemDetails?.discount_amt == 0 &&
          OrdDiscountType === 1
        ) {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        //Inv -> Scheme && Ord -> Fixed Price
        else if (InvDiscountType == "Scheme" && OrdDiscountType === 3) {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        //Inv -> Discount on PTR && Ord -> Fixed Price
        else if (
          InvDiscountType == "Discount Amount" ||
          (InvDiscountType == "Discount Percent" && selectedItemDetails?.discount_amt !== 0 && OrdDiscountType === 3)
        ) {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        } else {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        // if (modifiedQuantity == selectedItemDetails?.unmappedQty && modifiedFreeQty == selectedItemDetails?.unmappedFreeQty) {
        //   status = "fully_invoiced";
        // } else if (
        //   (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty)
        // ) {
        //   status = "partially_invoiced";
        // } else if (
        //   (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty)
        // ) {
        //   status = "fully_invoiced";
        // }

        // if (modifiedQuantity == selectedItemDetails?.unmappedQty && modifiedFreeQty == selectedItemDetails?.unmappedFreeQty) {
        //   status = "fully_invoiced";
        // } else if (
        //   (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) &&
        //   selectedOrders?.[0]?.price_identifier !== 2 &&
        //   selectedItemDetails?.discount_type !== "Discount Amount" &&
        //   selectedOrders?.[0]?.price_identifier !== 2 &&
        //   selectedItemDetails?.discount_type !== "Discount Percent"
        // ) {
        //   status = "partially_invoiced";
        // } else if (
        //   (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty &&
        //     selectedOrders?.[0]?.price_identifier === 2 &&
        //     selectedItemDetails?.discount_type === "Discount Amount") ||
        //   (selectedOrders?.[0]?.price_identifier === 2 && selectedItemDetails?.discount_type === "Discount Percent")
        // ) {
        //   status = "fully_invoiced";
        // }

        // if ((selectedItemDetails?.discount_type === "Discount Amount" || selectedItemDetails?.discount_type === "Discount Percent")
        //   && selectedItemDetails?.discount_amt !== 0
        //   && selectedOrders?.[0]?.price_identifier === 2) {
        //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
        //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
        // } else {
        //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
        //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
        // }

        return {
          ...item,
          mapped: true,
          merged: selectedProducts?.length > 0,
          unmappedQty,
          unmappedFreeQty,
          invoice_status: status,
        };
      })
    );

    setMappedData((prev) => [...prev, modifiedItem]);
    setIsMapped(false)  //M10-11117

    // Decrease the remaining quantity of the selected order
    setRemainingQuantity((prev) =>
      prev.map((item) => {
        const modifiedBaseKey = modifiedItem.unique_key?.split("~").slice(0, 2).join("~");
        const isMatchingItem = item.unique_key === modifiedBaseKey?.split("~")[0];

        if (!isMatchingItem) return item;

        return {
          ...item,
          orders: item.orders.map((order: any) => {
            if (order.order_code !== selectedOrders?.[0]?.id) return order;

            const isScheme =
              selectedItemDetails?.discount_type === "Scheme" &&
              (selectedOrders?.[0]?.price_identifier === 1 || selectedOrders?.[0]?.price_identifier === 3);

            return {
              ...order,
              remaining_quantity: isScheme
                ? order.remaining_quantity - modifiedItem.quantity - modifiedItem.free_qty
                : order.remaining_quantity - modifiedItem.quantity,
              remaining_free_quantity: isScheme ? order.remaining_free_quantity : order.remaining_free_quantity - modifiedItem.free_qty,
            };
          }),
          remaining_invoice_quantity: item?.remaining_invoice_quantity - modifiedItem.quantity,
          remaining_invoice_free_quantity: item?.remaining_invoice_free_quantity - modifiedItem.free_qty,
        };
      })
    );

    setSelectedProducts([]);
    setCheckedRows(new Set());
    setSelectedItemDetails(null);
    setTableData(DEFAULT_TABLE_DATA);
    setSelectedOrders([]);
    setSimilarOrders([]);
    setOrderToSelect([]);

    toast({
      title: "Data Mapped Successfully",
      description: "Item has been added to mapped data.",
    });
  };

  const handleReset = (invoiceApprovalData: any) => {
    setSelectedOrders([]);
    const data = invoiceApprovalData?.invoice?.invoiceProducts?.map((item: any) => ({
      ...item,
      unmappedQty: item?.quantity,
      unmappedFreeQty: item?.free_qty,
      unique_key: `${item?.item_code}~${item?.batch_no}`,
    }));
    setProductsData(data);
    setRemainingQuantity([]);
    setMappedData([]);
    setTableData(DEFAULT_TABLE_DATA);
    setSelectedItemDetails(null);
    setIsMapped(true) //M10-11117
    toast({
      title: "Data Reset Successfully",
      description: "All data has been reset.",
    });
  };

  const isRequestInProgress = useRef(false);

  const handleApproveInvoice = useDebounceLeading(async () => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;

    await withEventCheck(() => { }, `invoice_${invoiceApprovalData?.invoice?.invoice_id}`);
    const invoiceActionAllowedResponse = await getInvoiceActionAllowed(
      invoiceApprovalData?.invoice?.invoice_id,
      invoiceApprovalData?.stockist_invoice_attachment_id,
      "Approval",
      invoiceApprovalData?.company_code
    );
    if (!invoiceActionAllowedResponse?.data?.data?.allowed) {
      toast({ description: invoiceActionAllowedResponse?.data?.data?.message, variant: "destructive" });
      return;
    }
    if (mappedData.length == 0) {
      toast({ description: "Please add products", variant: "default" });
      return;
    }

    setIsApproving(true);

    const currentDate = new Date(invoiceApprovalData?.invoice?.new_invoice_date);
    const currentYear = currentDate.getFullYear();
    const financialYearStart = new Date(currentDate.getMonth() >= 3 ? currentYear : currentYear - 1, 3, 1);

    const invoiceAccYear = financialYearStart.getFullYear();

    const payloadToValidateInV1 = {
      cmpstockistcode: invoiceApprovalData?.company_stockist_code,
      invoicenumber_searchcolumn: invoiceApprovalData?.invoice?.invoice_number || "",
      invoice_accyear: invoiceAccYear,
      company_code: invoiceApprovalData?.company_code,
    };

    const response = await getInvoiceChangeValidationV1(payloadToValidateInV1);
    if (response?.data?.success !== "success") {
      toast({ description: response?.data?.message, variant: "destructive" });
      setIsApproving(false);
      return;
    }
    // const payload = {
    //   data: mappedData,
    //   primary_key: invoiceApprovalData?.invoice?.invoice_id,
    //   event_type: "INVOICE_PRODUCT_EDIT",
    // };

    try {
      // const invoiceApproveResponse = await editInvoiceProduct(payload);
      // if (invoiceApproveResponse) {
      const invoiceEditPayload = {
        data: {
          invoice_id: invoiceApprovalData?.invoice?.invoice_id,
          stockist_invoice_attachment_id: Number(stockist_invoice_attachment_id),
          cash_discount_perc: Number(invoiceApprovalData?.invoice?.cash_discount_perc),
          cash_discount: String(calculateTotalCashDiscount()),
          net_amount: String(invoiceApprovalData?.invoice?.net_amount),
          tcs_perc: Number(tcs),
          tcs_amt: ((calculateSubtotal() - (calculateSubtotal() * calculateTotalCashDiscount()) / 100 + calculateTotalGst()) * tcs) / 100,
          gst_amount: calculateTotalGst()?.toFixed(2),
          gross_amount: calculateSubtotal()?.toFixed(2),
          approval: true,
          // extra payload for backend versioining
          stockist_id: invoiceApprovalData?.stockist_id,
          invoice_number: invoiceApprovalData?.invoice?.invoice_number,
          // invoice header details sending for backend versioning
          u_code: invoiceApprovalData?.invoice?.u_code,
          new_invoice_date: invoiceApprovalData?.invoice?.new_invoice_date,
          // ...(qrIRN?.trim() !== "" && { e_inv_ref_type: qrIRN }),
          e_inv_ref_type: invoiceApprovalData?.invoice?.e_inv_ref_type,
          irn: invoiceApprovalData?.invoice?.irn,
          gst_validated: invoiceApprovalData?.invoice?.gst_validated,
          dl_validated: invoiceApprovalData?.invoice?.dl_validated,
          mci_validated: invoiceApprovalData?.invoice?.mci_validated,
          counter_gst_number: invoiceApprovalData?.counter_gst_number || "",
          counter_dl_number: invoiceApprovalData?.counter_dl_number || "",
          counter_mci_number: invoiceApprovalData?.counter_mci_number || "",
          invoice_num_modified: false,
          counter_data_validation: false,
          invoiceProduct: mappedData,
        },
        primary_key: invoiceApprovalData?.invoice?.invoice_id,
        event_type: "INVOICE_EDIT",
      };

      const invoiceEditResponse = await editInvoice(invoiceEditPayload, company_code);

      const invoiceStatusUpdateResponse = await withEventCheck(
        () => {
          return getInvoiceDetails(stockist_invoice_attachment_id as string, false, CompanyCode);
        },
        invoiceEditResponse?.data?.data?.products?.at(-1)?.eventStreamCreated?.streamId
      );
      console.log(invoiceStatusUpdateResponse);
      toast({ description: "Invoice approved successfully", variant: "default" });
      navigate("/transactions/invoice/1");
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message, variant: "destructive" });
    } finally {
      isRequestInProgress.current = false;
      setIsApproving(false);
    }
  }, 1000);

  const inputFieldValidationHandler = (item: any) => {
    const modifiedValue = Number(item?.modifiedData);

    const priceIdentifier = selectedOrders?.[0]?.price_identifier;
    const discountType = selectedItemDetails?.discount_type;

    if (item?.type === "Quantity") {
      const invoiceValue = Number(item?.invoiceData);
      const remainingItem = remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0]);
      const remainingOrderQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;
      // const remainingInvoiceQuantity = remainingItem?.remaining_invoice_quantity;
      const remainingInvoiceQuantity = selectedItemDetails?.unmappedQty;

      //Inv -> Scheme && Ord -> Discount on PTR
      if (discountType === "Scheme" && Number(modifiedValue) > Number(remainingOrderQuantity) && priceIdentifier === 1) {
        return true;
      }

      if (
        !(discountType === "Scheme" && priceIdentifier === 1) &&
        (Number(modifiedValue) > Number(invoiceValue) ||
          Number(modifiedValue) > Number(remainingOrderQuantity) ||
          Number(modifiedValue) > Number(remainingInvoiceQuantity))
      ) {
        return true;
      }
    } else if (item?.type === "Discount %") {
      const orderedValue = Number(item?.orderedData);
      if (modifiedValue > orderedValue) {
        return true;
      }
    } else if (item?.type === "Scheme Qty") {
      // const invoiceValue = Number(item?.invoiceData);
      // const remainingItem = remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0]);
      // const remainingFreeQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)
      // ?.remaining_free_quantity;
      // const remainingInvoiceFreeQuantity = remainingItem?.remaining_invoice_free_quantity;
      const allowedFreeQty = item?.allowed_free_qty;

      //Inv -> Discount on PTR && Ord -> Scheme
      if (
        (discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) !== 0 &&
        modifiedValue > allowedFreeQty &&
        priceIdentifier === 2
      ) {
        return true;
      }

      //Inv -> Fixed && Ord -> Scheme
      if (
        (discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) == 0 &&
        modifiedValue > allowedFreeQty &&
        priceIdentifier === 2
      ) {
        return true;
      }

      //Inv -> Scheme && Ord -> Discount on PTR
      if (discountType === "Scheme" && modifiedValue > allowedFreeQty && priceIdentifier === 1) {
        return true;
      }

      //Inv -> Fixed && Ord -> Discount on PTR
      if (
        (discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) == 0 &&
        modifiedValue > allowedFreeQty &&
        priceIdentifier === 1
      ) {
        return true;
      }

      //Inv -> Scheme && Ord -> Fixed Price
      if (discountType === "Scheme" && modifiedValue > allowedFreeQty && priceIdentifier === 3) {
        return true;
      }

      //Inv -> Discount on PTR && Ord -> Scheme
      if (
        (discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) !== 0 &&
        modifiedValue > allowedFreeQty &&
        priceIdentifier === 3
      ) {
        return true;
      }

      // if ((!(discountType === "Scheme" && priceIdentifier === 3)) &&
      //   (discountType !== "Discount Percent" && discountType !== "Discount Amount") &&
      //   (!(discountType === "Scheme" && priceIdentifier === 1)) &&
      //   (modifiedValue > invoiceValue || modifiedValue > remainingFreeQuantity || modifiedValue > remainingInvoiceFreeQuantity)
      // ) {
      //   return true;
      // }
    } else if (item?.type === "Price") {
      const invoiceValue = Number(item?.invoiceData);
      const orderedValue = Number(item?.orderedData);

      if (invoiceValue > orderedValue) {
        if (modifiedValue > invoiceValue) {
          return true;
        }
      } else {
        if (modifiedValue > orderedValue) {
          return true;
        }
      }
    } else if (item?.type === "Cash Discount") {
      const orderDiscount = Number(selectedOrders?.[0]?.order?.order_cash_discount_perc);
      const modifyDiscount = Number(item?.modifiedData);
      if (modifyDiscount > orderDiscount) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <InvoiceLayout>
      <Fragment>
        <Transition
          as={Fragment}
          show={isRemarksShown}
          enter="transition transition ease-in-out duration-200 transform"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition  ease-in-out duration-200 transform"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <div className="fixed right-0 inset-0 top-0 z-[500]">
            <InvoiceRemarks invoiceApprovalData={invoiceApprovalData} invoice_id={Number(invoiceApprovalData?.invoice?.invoice_id)} ref={ref} />
          </div>
        </Transition>
      </Fragment>
      <div className="mx-auto mt-10">
        <div className="flex flex-col px-[3.75rem] mt-6  pb-12 shadow-md border-b-[1px] border-b-[#E9E9E9]">
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col">
              <div
                className="flex items-center cursor-pointer font-semibold px-0 py-2 text-lg text-[#7F56D9]"
                onClick={() => navigate("/transactions/invoice/1")}
              >
                <img src={PurpleLeftArrowIcon} alt="back" className="w-3 h-3 mr-3" />
                Back to Invoices
              </div>
              <h1 className="text-[#49484C] font-bold text-[2rem]">Invoice Approval</h1>
            </div>
          </div>
          {/* //M10-11601 */}
          <div className="absolute right-4 flex flex-row gap-3 items-end mt-24">
            {/* Versioning */}
            <div
              onClick={() => setVersioningModal((prev) => !prev)}
            >
              <img src={Book} alt="Version" className="h-10 w-14 " />
            </div>

            {/* Remarks */}
            {viewRemarks && (
              <div
                onClick={() => setIsRemarksShown(true)}
                className="p-2 bg-white cursor-pointer"
              >
                <MessageCircle className="text-gray-500 h-6 w-6" />
              </div>
            )}
          </div>
        </div>

        <div className="p-5 mt-10 mx-[3.75rem] rounded-xl border border-[#d0d0d0] shadow-sm">
          <header className="w-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {invoiceApprovalData?.is_prioritised && (
                  <img src={star} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />
                )}
                {!invoiceApprovalData?.is_prioritised && (
                  <img src={starEmpty} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />
                )}
                <h1 className={"font-semibold text-lg text-[1.5rem] mr-4"}>
                  <span className="text-gray-400 text-[1.2rem]">Invoice# </span>
                  {invoiceApprovalData?.invoice?.invoice_number}
                </h1>
                <h1
                  className={`font-semibold  flex items-center text-xs h-6 px-2 rounded-full`}
                  style={{
                    backgroundColor: invoiceApprovalData?.fileStatusHdr?.color_code,
                    color: invoiceApprovalData?.fileStatusHdr?.text_color_code,
                  }}
                >
                  {invoiceApprovalData?.fileStatusHdr?.fuid_status_desc}
                  <img className="w-3 h-3 ml-1 cursor-pointer" src={downArrow} alt="down-arrow" />
                </h1>
              </div>
              <div className="flex gap-x-1.5">
                <Button
                  onClick={() => {
                    if (!invoiceApprovalData?.file_path) {
                      toast({ description: "Invoice not found", variant: "destructive" });
                      return;
                    }
                    downloadInvoice(invoiceApprovalData?.file_path, company_code).then((res: any) => {
                      if (res?.data?.success && res?.data?.data) {
                        window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                      } else {
                        console.error("Failed to download the invoice.");
                      }
                    });
                  }}
                  className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                >
                  <img src={Download} className="w-4 h-4 mr-1" alt="save-file" />
                  Invoice{" "}
                </Button>
                <Button
                  onClick={() => {
                    if (!invoiceApprovalData?.einv_attachment_path) {
                      toast({ description: "E-Invoice not found", variant: "destructive" });
                      return;
                    }
                    downloadInvoice(invoiceApprovalData?.einv_attachment_path, company_code).then((res: any) => {
                      if (res?.data?.success && res?.data?.data) {
                        window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                      } else {
                        console.error("Failed to download the E-Invoice.");
                      }
                    });
                  }}
                  className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                >
                  <img src={Download} className="w-4 h-4 mr-1" alt="send" />
                  E-Invoice{" "}
                </Button>
                {invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 6 && (
                  <Button
                    disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                    className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                    onClick={() => setRevertInvoiceOpen(true)}
                    module={fineGrainedPermissionsKeys.INVOICE.REVERTINVOICE.module}
                    action={fineGrainedPermissionsKeys.INVOICE.REVERTINVOICE.action}
                  >
                    Revert Invoice
                  </Button>
                )}
                {[8, 15].includes(invoiceApprovalData?.fileStatusHdr?.fuid_status_id) && (
                  <Button
                    className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                    onClick={() => setRecallInvoiceOpen(true)}
                    module={fineGrainedPermissionsKeys.INVOICE.REMOVEINVOICE.module}
                    action={fineGrainedPermissionsKeys.INVOICE.REMOVEINVOICE.action}
                  >
                    Remove Invoice
                  </Button>
                )}
                {/* <Button className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600">
                  <img src={cross} className="w-4 h-4 mr-1 invert" alt="cross" />
                  Clear{" "}
                </Button> */}
              </div>
            </div>
          </header>
          {VersioningModal && (
            <div ref={outSideClickRef}>
              <VersioningCrate type="Invoice" invoiceId={invoiceApprovalData?.invoice?.invoice_id} stockistInvAttachmentId={invoiceApprovalData?.stockist_invoice_attachment_id} />
            </div>
          )}
          <ul className="mt-5">
            <li className="my-5 mb-10 rounded-lg">
              <InvoiceDetailCard
                isExpandedInitial={true}
                isExpandable={false}
                status={invoiceApprovalData?.fileStatusHdr?.fuid_status_desc}
                checkDataType={false}
                data={getInvoiceDetailsInfo(invoiceApprovalData)}
                viewDetails={false}
                invoiceData={invoiceApprovalData}
                showHeader={false}
                fetchInvoiceDetails={fetchInvoiceDetails}
                tab={1}
                isMapped={isMapped} //M10-11117
              />
            </li>
          </ul>

          {
            <div className="px-4 mt-10 mb-10 rounded-lg">
              <div className="flex items-center gap-x-4 pb-5 border-b-2 border-gray-200">
                <p className="mb-2 text-lg font-semibold">Invoice Details</p>
                {invoiceApprovalData?.orderCodes?.length > 0 && (
                  <p className="text-sm text-gray-500 border border-gray-300 rounded-lg p-1 px-2">
                    Order Code: {invoiceApprovalData?.orderCodes?.map((code: any) => code?.order_code).join(", ")}
                  </p>
                )}
              </div>

              <table className="min-w-full mt-5 border border-gray-200">
                <thead className="w-full bg-gray-100">
                  <tr>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">S.No</th> {/* M10-9670 */}
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Select</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Product Type</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Item code</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Item description</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. MRP</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. PTR</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Price</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Qty.</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Gross Amt.</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Scheme</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Discount %</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Discount Amount</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Scheme Qty.</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">CD %</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">GST % </th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Batch</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Order Map</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Mapped</th>
                  </tr>
                </thead>
                <tbody>
                  {productsData?.sort((a: any, b: any) => (a?.textractProduct?.stockistfile_detail_code || 0) - (b?.textractProduct?.stockistfile_detail_code || 0))
                    ?.map((item: any, index: any) => (
                      <tr
                        key={index}
                        className={`${index % 2 === 0 ? "bg-gray-50 text-sm" : "text-sm"} ${item?.is_item_unknown ? "bg-[#F0F7FF]" : ""}`}
                      >
                        <td className="px-1 py-2 border-t">{index + 1}</td> {/* M10-9670 */}
                        {productsData?.filter((p) => p.unique_key === item.unique_key).length > 1 ? (
                          <td className="px-4 py-2 border-t">
                            <Checkbox checked={checkedRows.has(getRowId(item, index))} onChange={(e: any) => handleCheckboxChange(e, item, index)} />
                          </td>
                        ) : (
                          <td className="px-4 py-2 border-t"></td>
                        )}
                        <td className="px-1 py-2 border-t">{item?.is_item_unknown ? "UO" : "CP/OP"}</td>
                        <td className="px-1 py-2 border-t">{item?.item_code}</td>
                        <td className="px-1 py-2 border-t">{item?.item_description}</td>
                        <td className="px-1 py-2 border-t">{item?.inv_mrp}</td>
                        <td className="px-1 py-2 border-t">{item?.ptr}</td>
                        <td className="px-1 py-2 border-t">{item?.inv_rate}</td>
                        <td className="px-1 py-2 border-t">{item?.quantity}</td>
                        <td className="px-1 py-2 border-t">{item?.gross_amt}</td>
                        <td className="px-1 py-2 border-t">{item?.discount_type}</td>
                        <td className="px-1 py-2 border-t">{item?.discount_perc}</td>
                        <td className="px-1 py-2 border-t">{item?.discount_amt}</td>
                        <td className="px-1 py-2 border-t">{item?.free_qty}</td>
                        <td className="px-1 py-2 border-t">{item?.p_cash_discount_perc}</td>
                        <td className="px-1 py-2 border-t">{item?.p_gst_perc}</td>
                        <td className="px-1 py-2 border-t">{item?.batch_no}</td>
                        <td className="px-1 py-2 border-t">
                          <Button
                            disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                            onClick={() => handleViewDetails(selectedProducts.length > 0 ? selectedProducts?.[0] : item)}
                            className="w-fit h-8 font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0]"
                          >
                            View
                          </Button>
                        </td>
                        {/* <td className="px-4 py-2 border-t">
                      <Button onClick={() => handleProductDelete(item)}>
                        <img src={DeleteIcon} alt="delete" className="w-4 h-4" />
                      </Button>
                    </td> */}
                        {
                          <td className="px-4 py-2 border-t">
                            {item?.mapped && item?.invoice_status === "fully_invoiced" && (
                              <Button>
                                <img src={SuccessIcon} alt="counter_details_check" className="" />
                              </Button>
                            )}
                            {item?.mapped && item?.invoice_status === "partially_invoiced" && (
                              <Button>
                                <img src={PartialIcon} alt="counter_details_check" className="" />
                              </Button>
                            )}
                          </td>
                        }
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          }
          {orderedData?.length === 0 && (
            <div className="mt-10 border border-[#d0d0d0] p-5 rounded-xl mx-4">
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold">{selectedItemDetails?.item_description || "Select an Item"}</p>
                <div className="flex items-center gap-2">
                  <div className="w-[200px]">
                    <Autocomplete
                      openDropdownOnMount={showOrderDropdown}
                      inputClassName="w-full"
                      placeholder="Choose an order"
                      suggestionList={similarOrders?.map((item: any) => ({
                        id: item?.order?.order_code,
                        ...item,
                      }))}
                      currentSelected={selectedOrders?.[0]?.id || ""}
                      defaultValue={selectedOrders?.[0]?.id || ""}
                      uniqueKey="id"
                      displayKey="id"
                      onSelectionChange={(value: any) => {
                        const differentOrder = !invoiceApprovalData?.orderCodes?.some((order: any) => order?.order_code === value?.id);
                        if (differentOrder) {
                          setIsDifferentOrderModalOpen(true);
                          setOrderToSelect([value]);
                        } else {
                          setSelectedOrders([value]);
                          setShowOrderDropdown(false);
                        }
                      }}
                      inputRef={inputRef}
                    />
                  </div>
                  <div className="p-2 bg-[#F1F1F1] border-2 border-gray-200 rounded-md cursor-pointer" onClick={handleToggle}>
                    <img className={`transition-transform  duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
                  </div>
                </div>
              </div>
              <div
                className={`transition-all items-center duration-500 ease-in-out ${isExpanded ? "opacity-100 max-h-[1000px]" : "max-h-0 opacity-0"}`}
                style={{ transitionProperty: "max-height, opacity" }}
              >
                <table className="min-w-full mt-5 border border-gray-200">
                  <thead className="w-full bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600"></th>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">Invoice Data</th>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">
                        Ordered Data - <span className="text-[#8051FF]">{selectedOrders?.[0]?.id}</span>
                      </th>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">Modified Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((item, index) => (
                      <tr key={index} className="h-12 text-sm">
                        <td className="px-4 py-2 text-gray-500 border-t">{item?.type}</td>
                        <td className={`px-4 py-2 border-t ${item.type === "Calculated Value" ? "text-red-600" : ""}`}>
                          {Number(item?.invoiceData)}
                        </td>
                        <td className="px-4 py-2 border-t">
                          {item?.orderedData}{" "}
                          {selectedOrders?.length > 0 && item?.type === "Quantity" && (
                            <span className="text-[#8051FF]">
                              (Remaining Qty:{" "}
                              {
                                remainingQuantity
                                  ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
                                  ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity
                              }
                              )
                            </span>
                          )}
                          {selectedOrders?.length > 0 && item?.type === "Scheme Qty" && (
                            <span className="text-[#8051FF]">
                              (Remaining Free Qty:{" "}
                              {remainingQuantity
                                ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
                                ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity || 0}
                              )
                            </span>
                          )}
                        </td>
                        <td className="flex gap-2 px-4 py-2 border-t">
                          {item.type === "Calculated Value" ? (
                            <span className="w-full px-2 py-1 border-black text-red-600">
                              {Number(item?.modifiedData)} (
                              {Number(((Number(item?.modifiedData) - Number(item?.invoiceData)) / Number(item?.modifiedData)) * 100).toFixed(2)}%)
                            </span>
                          ) : (
                            <Input
                              disabled={item.type === "MRP" || item.type === "PTR"}
                              type="number"
                              placeholder="0"
                              enableArrows={false}
                              value={Number(item?.modifiedData)}
                              onChange={(e) => {
                                handleModifyData(index, e.target.value);
                              }}
                              onWheel={(e) => (e.target as HTMLInputElement).blur()}
                              className="w-full px-2 py-1 border rounded-md border-1-black"
                              isError={inputFieldValidationHandler(item)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                {selectedItemDetails && (
                  <Button onClick={handleSaveModifiedData} className="mt-4 h-9 font-normal text-white bg-[#8051FF] shadow-sm">
                    Save Modified Data
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {orderedData?.length === 0 && (
          <div className="mt-10 border mx-[3.75rem] border-[#d0d0d0] p-5 rounded-xl px-10">

            <div className="flex items-center justify-between pb-5">
              <p className="text-lg font-semibold">Final Mapped Data</p>
              {/* <Button className="w-10 h-10 bg-[#F1F1F1] border border-1-black" onClick={handleToggle}>
            <img className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
          </Button> */}
              <Button
                disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                onClick={() => handleReset(invoiceApprovalData)}
              >
                Reset
              </Button>
            </div>

            <table className="min-w-full border border-gray-200">
              <thead className="w-full bg-gray-100">
                <tr>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">S.No</th> {/* M10-9670 */}
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Item Code</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Order Code</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Item description</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. MRP</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. PTR</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Price</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Qty.</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Gross Amt.</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Scheme</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Discount %</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Cash Discount %</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Scheme Qty.</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">GST % </th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Batch</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {mappedData
                  ?.sort((a: any, b: any) => (a?.textractProduct?.stockistfile_detail_code || 0) - (b?.textractProduct?.stockistfile_detail_code || 0))
                  ?.map((item: any, index: any) => (
                    <tr
                      key={index}
                      className={`text-sm h-12`}
                      style={{ backgroundColor: uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")] }}
                    >
                      <td className="px-1 py-2 border-t">{index + 1}</td> {/* M10-9670 */}
                      <td className="px-1 py-2 border-t">{item?.item_code}</td>
                      <td className="px-1 py-2 border-t">{item?.order_code}</td>
                      <td className="px-1 py-2 border-t">{item?.item_description}</td>
                      <td className="px-1 py-2 border-t">{item?.inv_mrp}</td>
                      <td className="px-1 py-2 border-t">{item?.ptr}</td>
                      <td className="px-1 py-2 border-t">{item?.inv_rate}</td>
                      <td className="px-1 py-2 border-t">{item?.quantity}</td>
                      <td className="px-1 py-2 border-t">{Number(item?.inv_rate * item?.quantity).toFixed(2)}</td>
                      <td className="px-1 py-2 border-t">{item?.discount_type}</td>
                      <td className="px-1 py-2 border-t">{Number(item?.discount_perc).toFixed(2)}</td>
                      <td className="px-1 py-2 border-t">{item?.p_cash_discount_perc}</td>
                      <td className="px-1 py-2 border-t">{item?.free_qty}</td>
                      <td className="px-1 py-2 border-t">{item?.p_gst_perc || 0}</td>
                      <td className="px-1 py-2 border-t">{item?.batch_no}</td>
                      <td className="px-1 py-2 border-t">
                        <Button
                          className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                          onClick={() => {
                            const modifiedBaseKey = item.unique_key;
                            // Filter the mappedData to exclude the current item
                            const filteredMappedData = mappedData.filter((product: any) => product?.unique_key !== item?.unique_key);
                            // Check if another item with same modifiedBaseKey exists
                            const hasSameKeyInMapped = filteredMappedData.some((product: any) => product?.unique_key === modifiedBaseKey);
                            // Update productsData only if no same key exists in remaining mappedData

                            if (!hasSameKeyInMapped) {
                              setProductsData((prev) =>
                                prev.map((product) => {
                                  if (product?.unique_key?.split("~")[0] !== modifiedBaseKey?.split("~")[0]) return product;
                                  const updatedUnmappedQty = product?.merged
                                    ? Number(product?.quantity)
                                    : Number(product?.unmappedQty) + Number(item?.quantity);
                                  const updatedUnmappedFreeQty = product?.merged
                                    ? Number(product?.free_qty)
                                    : Number(product?.unmappedFreeQty) + Number(item?.free_qty);

                                  let status = "fully_invoiced";

                                  if (updatedUnmappedQty === Number(product.quantity) && updatedUnmappedFreeQty === Number(product.free_qty)) {
                                    status = "not_invoiced";
                                  } else if (updatedUnmappedQty < product.quantity || updatedUnmappedFreeQty < product.free_qty) {
                                    status = "partially_invoiced";
                                  }

                                  return {
                                    ...product,
                                    mapped: status === "not_invoiced" ? false : true,
                                    unmappedQty: updatedUnmappedQty,
                                    unmappedFreeQty: updatedUnmappedFreeQty,
                                    invoice_status: status,
                                  };
                                })
                              );
                            }

                            // Remove the item from mappedData
                            setMappedData((prev: any[]) => prev?.filter((product: any) => product?.unique_key !== item?.unique_key));
                            // Update remaining quantity
                            setRemainingQuantity((prev: any[]) =>
                              prev.map((product: any) => {
                                const modifiedBaseKey = item.unique_key?.split("~").slice(0, 2).join("~");

                                return product?.unique_key === modifiedBaseKey?.split("~")[0]
                                  ? {
                                    ...product,

                                    orders:
                                      product?.orders?.map((order: any) =>
                                        order?.order_code === item?.order_code?.[0]
                                          ? {
                                            ...order,
                                            remaining_quantity: order?.remaining_quantity + item?.quantity,
                                            remaining_free_quantity: order?.remaining_free_quantity + item?.free_qty,
                                          }
                                          : order
                                      ) ?? [],
                                    remaining_invoice_quantity: product?.remaining_invoice_quantity + item?.quantity,
                                    remaining_invoice_free_quantity: product?.remaining_invoice_free_quantity + item?.free_ty,
                                  }
                                  : product;
                              })
                            );
                            //M10-11117
                            if (mappedData.length == 1) {
                              setIsMapped(true)
                            }
                            //M10-11117
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        {orderedData?.length > 0 && <InvoiceApprovalOrderedData orderData={orderedData} />}
        <div className="p-6 mt-10 rounded-xl border border-[#d0d0d0] w-[360px] shadow-sm mx-[3.75rem]">
          <p className="mb-2 text-lg font-semibold">Calculations</p>
          <div className="flex flex-col pb-10 gap-y-1">
            <p className="flex justify-between py-2 text-sm">
              Entry Net Amount <span className="">₹ {Number(entryNetAmt).toLocaleString("en-IN")}</span>
            </p>
            <p className="flex justify-between py-2 text-sm">
              Subtotal{" "}
              <span className="">
                ₹ {orderedData?.length > 0 ? invoiceApprovalData?.invoice?.gross_amount : Number(calculateSubtotal().toFixed(2))}
              </span>
            </p>
            <p className="flex justify-between py-2 text-sm">
              <span className="mr-2">Cash Discount %</span>
              <div className="">
                <Input
                  type="number"
                  enableArrows={false}
                  showIcon={false}
                  disabled={true}
                  placeholder="0"
                  value={
                    orderedData?.length > 0 ? invoiceApprovalData?.invoice?.cash_discount_perc : calculateTotalCashDiscountPercentage()?.toFixed(2)
                  }
                  className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                />
              </div>
            </p>
            <p className="flex justify-between py-2 text-sm">
              <span className="mr-2">Cash Discount Amount</span>
              <div className="">
                <Input
                  type="number"
                  enableArrows={false}
                  showIcon={false}
                  disabled={true}
                  value={orderedData?.length > 0 ? invoiceApprovalData?.invoice?.cash_discount : calculateTotalCashDiscount()?.toFixed(2)}
                  className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                />
              </div>
            </p>
            <p className="flex justify-between py-2 text-sm">
              GST Amount
              <span className="font-semibold">
                ₹{orderedData?.length > 0 ? invoiceApprovalData?.invoice?.gst_amount : calculateTotalGst()?.toFixed(2)}
              </span>
            </p>
            <p className="flex justify-between py-2 text-sm">
              <span className="mr-2">TCS %</span>
              <div className="">
                <Input
                  enableArrows={false}
                  showIcon={false}
                  disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                  type="number"
                  placeholder="TCS"
                  value={orderedData?.length > 0 ? invoiceApprovalData?.invoice?.tcs_perc : tcs}
                  // value={Number(invoiceApprovalData?.invoice?.tcs_perc)}
                  onChange={(e: any) => setTcs(e?.target?.value)}
                  className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                />{" "}
              </div>
            </p>
            <p className="flex justify-between py-2 text-sm">
              Total (as calculated){" "}
              <span className="font-semibold">
                ₹{" "}
                {
                  calculateTotalAsFinal() ? Number(calculateTotalAsFinal().toFixed(2)) : Number(calculateTotal().toFixed(2))}
              </span>
            </p>
            {/* <p className="flex justify-between py-2 text-sm">
              Net Inv. Amt. <span className="">₹ {calculateSubtotal().toFixed(2)}</span>
            </p> */}
            {/* M10-10941 Only (Waiting For Approval)*/}
            {invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 6 && (
              <Button
                disabled={isRequestInProgress.current}
                loaderSize="small"
                isLoading={isApproving}
                className="w-full h-9 mt-5 font-normal text-white bg-[#8051FF] shadow-sm"
                onClick={() => {
                  if (mappedData?.length == 0) {
                    toast({ description: "Please add products before approval", variant: "destructive" });
                    return;
                  }
                  if (Math.abs(Number(entryNetAmt) - Number(calculateTotal())) > 10) {
                    setOpenNetAmtModal(true);
                  } else {
                    handleApproveInvoice();
                  }
                }}
                module={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].module}
                action={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].action}
              >
                Approve Invoice
              </Button>
            )}
          </div>
        </div>
      </div>
      <Modal isOpen={revertInvoiceOpen} isHeader={false} onCloseModal={() => setRevertInvoiceOpen(false)}>
        <RevertInvoice
          onSuccess={() => {
            navigate("/transactions/invoice/1");
          }}
          onCloseModal={() => setRevertInvoiceOpen(false)}
        />
      </Modal>
      <Modal isOpen={recallInvoiceOpen} isHeader={false} onCloseModal={() => setRecallInvoiceOpen(false)}>
        <RecallInvoice
          onSuccess={() => {
            navigate("/transactions/invoice/1");
          }}
          onCloseModal={() => setRecallInvoiceOpen(false)}
        />
      </Modal>
      <Modal isOpen={openNetAmtModal} isHeader={false} onCloseModal={() => setOpenNetAmtModal(false)} title="Escalate Invoice">
        <NetAmountCompare
          handleApproveInvoice={handleApproveInvoice}
          calculatedTotal={calculateTotal().toFixed(2)}
          entryNetAmount={entryNetAmt}
          onCloseModal={() => setOpenNetAmtModal(false)}
          isLoading={isApproving}
        />
      </Modal>
      <Modal isOpen={isDifferentOrderModalOpen} isHeader={false} onCloseModal={() => setIsDifferentOrderModalOpen(false)} title="Is Different Order">
        <IsDifferentOrder
          handleApproveInvoice={() => {
            setSelectedOrders(orderToSelect);
            setShowOrderDropdown(false);
            setIsDifferentOrderModalOpen(false);
          }}
          onCloseModal={() => {
            setOrderToSelect([]);
            setIsDifferentOrderModalOpen(false);
          }}
          invoiceOrderCode={invoiceApprovalData?.orderCodes?.map((order: any) => order?.order_code)?.join(",")}
          selectedOrderCode={orderToSelect?.[0]?.id}
        />
      </Modal>
    </InvoiceLayout>
  );
};
